import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { FlowPoint } from 'components/IncidentConfig/roster/rosterEdgeAttach';
import {
  viewportForBounds,
  type ChartBounds,
} from 'components/IncidentConfig/captureOrgChartImage';

export type ChartViewport = { x: number; y: number; zoom: number };

export const CHART_MIN_ZOOM = 0.15;
export const CHART_MAX_ZOOM = 2;
const FIT_PADDING = 0.1;
/** Wheel delta to zoom factor. 0.006 is about 4× the old trackpad crawl. */
const WHEEL_ZOOM_SENSITIVITY = 0.006;
/** Keypad +/− and double-click step. 2.25 is a 125% change per click. */
export const BUTTON_ZOOM_STEP = 2.25;

export const clampZoom = (zoom: number) =>
  Math.min(CHART_MAX_ZOOM, Math.max(CHART_MIN_ZOOM, zoom));

export function nextButtonZoom(zoom: number, direction: 'in' | 'out'): number {
  return clampZoom(
    direction === 'in' ? zoom * BUTTON_ZOOM_STEP : zoom / BUTTON_ZOOM_STEP
  );
}

/**
 * Pan and zoom for the org chart canvas: the translate-and-scale applied to the
 * chart layer, wheel zoom about the cursor, and fitting a set of bounds into
 * the container. Dragging is left to the canvas, which also has to decide
 * whether a drag is a pan or a selection rectangle.
 */
export function useChartViewport(containerRef: RefObject<HTMLElement | null>) {
  const [viewport, setViewport] = useState<ChartViewport>({
    x: 0,
    y: 0,
    zoom: 1,
  });
  // Event handlers read the latest viewport without re-binding on every pan.
  const viewportRef = useRef(viewport);
  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  /** Zoom keeping the container point `anchor` (container-local) fixed. */
  const zoomAt = useCallback((nextZoomRaw: number, anchor: FlowPoint) => {
    setViewport((current) => {
      const nextZoom = clampZoom(nextZoomRaw);
      if (nextZoom === current.zoom) return current;
      const ratio = nextZoom / current.zoom;
      return {
        zoom: nextZoom,
        x: anchor.x - (anchor.x - current.x) * ratio,
        y: anchor.y - (anchor.y - current.y) * ratio,
      };
    });
  }, []);

  const containerCenter = useCallback((): FlowPoint => {
    const element = containerRef.current;
    if (!element) return { x: 0, y: 0 };
    return { x: element.clientWidth / 2, y: element.clientHeight / 2 };
  }, [containerRef]);

  const zoomInAt = useCallback(
    (anchor: FlowPoint) =>
      zoomAt(nextButtonZoom(viewportRef.current.zoom, 'in'), anchor),
    [zoomAt]
  );
  const zoomIn = useCallback(
    () => zoomInAt(containerCenter()),
    [zoomInAt, containerCenter]
  );
  const zoomOut = useCallback(
    () =>
      zoomAt(nextButtonZoom(viewportRef.current.zoom, 'out'), containerCenter()),
    [zoomAt, containerCenter]
  );

  const panBy = useCallback((dx: number, dy: number) => {
    setViewport((current) => ({
      ...current,
      x: current.x + dx,
      y: current.y + dy,
    }));
  }, []);

  const fitBounds = useCallback(
    (bounds: ChartBounds | null) => {
      const element = containerRef.current;
      if (!element || !bounds) return;
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (width === 0 || height === 0) return;
      setViewport(
        viewportForBounds(
          bounds,
          width,
          height,
          CHART_MIN_ZOOM,
          CHART_MAX_ZOOM,
          FIT_PADDING
        )
      );
    },
    [containerRef]
  );

  /** Container-local pixel coordinates of a pointer event. */
  const toContainerPoint = useCallback(
    (clientX: number, clientY: number): FlowPoint => {
      const element = containerRef.current;
      if (!element) return { x: clientX, y: clientY };
      const rect = element.getBoundingClientRect();
      return { x: clientX - rect.left, y: clientY - rect.top };
    },
    [containerRef]
  );

  // Wheel has to be a native, non-passive listener or the page scrolls along
  // with the zoom.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const current = viewportRef.current;
      const factor = Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY);
      zoomAt(
        current.zoom * factor,
        toContainerPoint(event.clientX, event.clientY)
      );
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [containerRef, zoomAt, toContainerPoint]);

  return useMemo(
    () => ({
      viewport,
      viewportRef,
      zoomIn,
      zoomInAt,
      zoomOut,
      panBy,
      fitBounds,
      toContainerPoint,
    }),
    [viewport, zoomIn, zoomInAt, zoomOut, panBy, fitBounds, toContainerPoint]
  );
}
